import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';

export async function exportCardAsPng(elementId, filename) {
  const el = document.getElementById(elementId);
  if (!el) return;

  const brushes = el.querySelectorAll('.recharts-brush');
  const originalDisplays = [];
  brushes.forEach(brush => {
    originalDisplays.push(brush.style.display);
    brush.style.display = 'none';
  });

  try {
    const canvas = await html2canvas(el, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = imgData;
    link.download = `${filename}.png`;
    link.click();
  } finally {
    brushes.forEach((brush, i) => {
      brush.style.display = originalDisplays[i];
    });
  }
}

/**
 * Export a chart/table card as PDF (Req 4.3.2)
 * @param {string} elementId - DOM id of the card to export
 * @param {string} filename - e.g. "RevenueAnalysis_NetRevenuePerUnit_Jan2026-Mar2026"
 * @param {string} filterSummary - active filter text to include
 */
export async function exportCardAsPdf(elementId, filename, filterSummary = '') {
  const el = document.getElementById(elementId);
  if (!el) return;

  const brushes = el.querySelectorAll('.recharts-brush');
  const originalDisplays = [];
  brushes.forEach(brush => {
    originalDisplays.push(brush.style.display);
    brush.style.display = 'none';
  });

  try {
    const canvas = await html2canvas(el, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('l', 'mm', 'a4');
    const w = pdf.internal.pageSize.getWidth() - 20;
    const h = (canvas.height * w) / canvas.width;
    if (filterSummary) {
      pdf.setFontSize(9);
      pdf.text(`Filters: ${filterSummary}`, 10, 8);
    }
    pdf.addImage(imgData, 'PNG', 10, filterSummary ? 12 : 5, w, h);
    pdf.save(`${filename}.pdf`);
  } finally {
    brushes.forEach((brush, i) => {
      brush.style.display = originalDisplays[i];
    });
  }
}

/**
 * Export full dashboard as PDF (Req 4.3.2)
 */
export async function exportFullDashboardPdf(containerId, filename, filterSummary = '') {
  const el = document.getElementById(containerId);
  if (!el) return;

  const brushes = el.querySelectorAll('.recharts-brush');
  const originalDisplays = [];
  brushes.forEach(brush => {
    originalDisplays.push(brush.style.display);
    brush.style.display = 'none';
  });

  try {
    const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, scrollY: -window.scrollY });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('l', 'mm', 'a4');
    const w = pdf.internal.pageSize.getWidth() - 20;
    const h = (canvas.height * w) / canvas.width;
    const pageH = pdf.internal.pageSize.getHeight() - 15;
    if (filterSummary) { pdf.setFontSize(9); pdf.text(`Filters: ${filterSummary}`, 10, 8); }
    let y = filterSummary ? 12 : 5;
    if (h <= pageH) {
      pdf.addImage(imgData, 'PNG', 10, y, w, h);
    } else {
      // Multi-page
      let remaining = h;
      let srcY = 0;
      while (remaining > 0) {
        const sliceH = Math.min(remaining, pageH);
        pdf.addImage(imgData, 'PNG', 10, y, w, h, undefined, undefined, 0);
        remaining -= pageH;
        if (remaining > 0) pdf.addPage();
      }
    }
    pdf.save(`${filename}.pdf`);
  } finally {
    brushes.forEach((brush, i) => {
      brush.style.display = originalDisplays[i];
    });
  }
}

/**
 * Export table data as Excel sheet (.xlsx) preserving UI formats, headers, and data types.
 */
export function exportTableAsCsv(data, columns, filename) {
  const fileExt = '.xlsx';

  // Helper to extract text content from a React element recursively
  function getReactText(el) {
    if (el == null) return '';
    if (typeof el !== 'object') return String(el);
    if (el.props && el.props.children !== undefined) {
      if (Array.isArray(el.props.children)) {
        return el.props.children.map(getReactText).join('');
      }
      return getReactText(el.props.children);
    }
    return '';
  }

  // 1. Process headers: map each column to a key and replace '₹' with 'Rs'
  const headerKeys = columns.map((_, idx) => `col_${idx}`);
  const headerRow = {};
  columns.forEach((c, idx) => {
    let title = c.title || '';
    let titleStr = typeof title === 'string' ? title : getReactText(title);
    headerRow[`col_${idx}`] = titleStr.replace(/₹/g, 'Rs');
  });

  // 2. Process rows and determine native Excel types and formats
  const rows = data.map(row => {
    const rowObj = {};
    columns.forEach((c, idx) => {
      let rawVal = row[c.dataIndex];
      let displayVal = rawVal;
      if (c.render) {
        displayVal = c.render(rawVal, row);
      }
      
      const text = getReactText(displayVal).trim();
      const colKey = `col_${idx}`;

      // Check if it's empty or placeholder
      if (text === '-' || text === '—' || text === '') {
        rowObj[colKey] = { v: '', t: 's' };
        return;
      }

      const titleLower = (c.title ? getReactText(c.title).toLowerCase() : '');
      const keyLower = String(c.dataIndex || '').toLowerCase();

      // a. Block Number or Integer
      if (titleLower.includes('block number') || keyLower === 'block' || titleLower === 'block') {
        const intVal = parseInt(text, 10);
        if (!isNaN(intVal)) {
          rowObj[colKey] = { v: intVal, t: 'n', z: '0' };
          return;
        }
      }

      // b. Date formatting
      const isDateCol = titleLower.includes('date') || keyLower.includes('date') || keyLower.includes('period') || /^\d{4}-\d{2}-\d{2}$/.test(text);
      if (isDateCol) {
        const dateMatch = text.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
        if (dateMatch) {
          const jsDate = new Date(Date.UTC(parseInt(dateMatch[1]), parseInt(dateMatch[2]) - 1, parseInt(dateMatch[3])));
          rowObj[colKey] = { v: jsDate, t: 'd', z: 'yyyy-mm-dd' };
          return;
        }
      }

      // c. Time Block formatting (e.g. 00:00, 00:15)
      const isTimeCol = titleLower.includes('time') || keyLower.includes('time') || /^\d{2}:\d{2}(:\d{2})?$/.test(text);
      if (isTimeCol) {
        rowObj[colKey] = { v: text, t: 's' };
        return;
      }

      // d. Numeric Formatting (Float/Number)
      let cleanText = text.replace(/[₹%,]/g, '').replace(/Rs/gi, '').replace(/MWh/gi, '').replace(/MW/gi, '').trim();
      if (!isNaN(cleanText) && cleanText !== '') {
        let numVal = parseFloat(cleanText);
        const isIntCol = isIntegerColumnKey(titleLower) || isIntegerColumnKey(keyLower);
        
        if (!isIntCol) {
          numVal = Math.round(numVal * 100) / 100;
        }
        
        let formatStr = isIntCol ? '0' : '0.00';
        rowObj[colKey] = { v: numVal, t: 'n', z: formatStr };
        return;
      }

      // Default fallback
      rowObj[colKey] = { v: text, t: 's' };
    });
    return rowObj;
  });

  // 3. Create worksheet with custom cell types
  const ws = XLSX.utils.json_to_sheet([headerRow, ...rows], {
    header: headerKeys,
    skipHeader: true,
  });

  // Keep first header row as string type
  columns.forEach((_, idx) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx });
    if (ws[cellRef]) {
      ws[cellRef].t = 's';
    }
  });

  applyExcelNumberFormat(ws);

  // 4. Write XLSX binary file
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${filename}${fileExt}`);
}

/**
 * Helper to check if a column key or header title represents an integer column (like Year, Month, Day, Hour, Block, Container Count).
 * Uses explicit term matching and strict word boundaries for 'id' so words like 'grid' or 'hybrid' are NOT false-matched as integers.
 */
export const isIntegerColumnKey = (keyOrTitle) => {
  if (!keyOrTitle) return false;
  const s = String(keyOrTitle).toLowerCase().trim();
  const explicitTerms = ["year", "month", "block", "day", "hour", "container", "count", "sr no", "index", "no."];
  if (explicitTerms.some((term) => s.includes(term))) {
    return true;
  }
  // Match 'id' only as standalone word or boundary (e.g., 'simulation_id', 'id', 'user_id'), but NOT inside words like 'grid' or 'hybrid'
  if (/\b(id|simulation_id|project_id|user_id|sim_id)\b/i.test(s) || s === 'id' || s.endsWith('_id') || s.startsWith('id_')) {
    if (!s.includes("grid") && !s.includes("hybrid") && !s.includes("valid") && !s.includes("feeder")) {
      return true;
    }
  }
  return false;
};

/**
 * Ensures all numerical data in Excel exports show at most 2 decimal places.
 * Integers and integer columns (like Year, Month, Block, Day, Hour, Container Count) are preserved as integers.
 */
export const formatValueForExcel = (val, key = "") => {
  if (val === null || val === undefined) return val;

  const isIntegerKey = isIntegerColumnKey(key);

  if (typeof val === "number") {
    if (isIntegerKey) {
      return Math.round(val);
    }
    return Math.round(val * 100) / 100;
  }

  if (typeof val === "string") {
    const trimmed = val.trim();
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(trimmed) || /^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return val;
    }
    if (!isNaN(trimmed) && trimmed !== "") {
      const num = parseFloat(trimmed);
      if (isIntegerKey) {
        return Math.round(num);
      }
      return Math.round(num * 100) / 100;
    }
  }

  return val;
};

/**
 * Applies explicit Excel number formatting ('0.00') to numeric cells in a worksheet,
 * ensuring numeric columns ALWAYS display decimal places (0.00) in Excel downloads,
 * except for integer columns (Year, Month, Block, Day, Hour, Container Count, etc.).
 */
export const applyExcelNumberFormat = (ws) => {
  if (!ws || !ws["!ref"]) return ws;

  const range = XLSX.utils.decode_range(ws["!ref"]);

  for (let C = range.s.c; C <= range.e.c; ++C) {
    const headerCell = ws[XLSX.utils.encode_cell({ r: range.s.r, c: C })];
    const headerVal = headerCell ? String(headerCell.v || "").toLowerCase().trim() : "";
    const isIntegerCol = isIntegerColumnKey(headerVal);

    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellRef];
      if (!cell) continue;

      if (isIntegerCol) {
        if (cell.t === "n") {
          cell.z = "0";
        }
      } else {
        if (cell.t === "n") {
          cell.z = "0.00";
        } else if (cell.v !== null && cell.v !== undefined && cell.v !== "" && !isNaN(cell.v) && typeof cell.v !== "boolean") {
          const num = typeof cell.v === "number" ? cell.v : parseFloat(cell.v);
          if (!isNaN(num)) {
            cell.t = "n";
            cell.v = Math.round(num * 100) / 100;
            cell.z = "0.00";
          }
        }
      }
    }
  }

  return ws;
};

export const formatRowsForExcel = (rows) => {
  if (!Array.isArray(rows)) return rows;
  return rows.map((row) => {
    if (Array.isArray(row)) {
      return row.map((cell) => formatValueForExcel(cell));
    }
    if (typeof row === "object" && row !== null) {
      const newRow = {};
      for (const [k, v] of Object.entries(row)) {
        newRow[k] = formatValueForExcel(v, k);
      }
      return newRow;
    }
    return row;
  });
};

